import React, { useState } from "react";

import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useNotifications } from "../contexts/NotificationContext";
import { buildDetailNavigationState } from "../utils/navigation";

const Notifications = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    notifications,
    markAsRead,
    clearNotifications,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();

  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [filter, setFilter] = useState("all"); // all, unread, read
  const [typeFilter, setTypeFilter] = useState("all"); // all, new_ticket, ticket_reply, etc.

  // Filter notifications based on selected filters
  const filteredNotifications = notifications.filter((notification) => {
    if (filter === "unread" && notification.read) return false;
    if (filter === "read" && !notification.read) return false;
    if (typeFilter !== "all" && notification.type !== typeFilter) return false;

    return true;
  });

  // Calculate stats
  const unreadCount = notifications.filter((n) => !n.read).length;
  const totalCount = notifications.length;

  const handleNotificationClick = async (notification) => {
    // Mark as read first
    await markAsRead(notification.id);

    // Navigate based on notification type with error handling
    try {
      switch (notification.type) {
        case "new_ticket":
        case "ticket_reply":
        case "ticket_closed":
        case "ticket_assigned":
        case "ticket_updated":
          const ticketId = notification.ticketNumber || notification.ticketId;

          if (ticketId) {
            navigate(`/tickets/${ticketId}`);
          } else {
            toast.warning("Ticket information not available");
          }
          break;
        case "invoice":
          if (notification.invoiceId) {
            navigate(`/invoices/${notification.invoiceId}`, {
              state: buildDetailNavigationState(location),
            });
          } else {
            toast.warning("Invoice information not available");
          }
          break;
        case "customer":
          if (notification.customerId) {
            navigate(`/customers/${notification.customerId}`);
          } else {
            toast.warning("Customer information not available");
          }
          break;
        default:
          break;
      }
    } catch (error) {
      console.error("Error navigating from notification:", error);
      toast.error("Unable to open this item");
    }
  };

  const handleDeleteNotification = (id) => {
    if (deleteNotification) {
      deleteNotification(id);
      toast.success("Notification deleted");
    }
  };

  const handleDeleteSelected = () => {
    if (selectedNotifications.length === 0) {
      toast.warning("No notifications selected");

      return;
    }

    selectedNotifications.forEach((id) => {
      if (deleteNotification) {
        deleteNotification(id);
      }
    });

    toast.success(`${selectedNotifications.length} notifications deleted`);
    setSelectedNotifications([]);
  };

  const handleSelectAll = () => {
    if (selectedNotifications.length === filteredNotifications.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(filteredNotifications.map((n) => n.id));
    }
  };

  const handleSelectNotification = (id) => {
    if (selectedNotifications.includes(id)) {
      setSelectedNotifications(
        selectedNotifications.filter((nId) => nId !== id)
      );
    } else {
      setSelectedNotifications([...selectedNotifications, id]);
    }
  };

  const handleClearAll = () => {
    if (notifications.length === 0) {
      toast.info("No notifications to clear");

      return;
    }

    clearNotifications();
    toast.success("All notifications cleared");
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "new_ticket":
        return { icon: "fas fa-ticket-alt", bg: "bg-blue-500" };
      case "ticket_reply":
        return { icon: "fas fa-comment", bg: "bg-purple-500" };
      case "ticket_closed":
        return { icon: "fas fa-check-circle", bg: "bg-green-500" };
      case "invoice":
        return { icon: "fas fa-file-invoice-dollar", bg: "bg-yellow-500" };
      case "payment":
        return { icon: "fas fa-credit-card", bg: "bg-green-500" };
      case "customer":
        return { icon: "fas fa-user-plus", bg: "bg-indigo-500" };
      case "system":
        return { icon: "fas fa-cog", bg: "bg-gray-500" };
      default:
        return { icon: "fas fa-bell", bg: "bg-gray-500" };
    }
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diff = Math.floor((now - time) / 1000); // difference in seconds

    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;

    return time.toLocaleDateString();
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Notifications
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {unreadCount} unread, {totalCount} total
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          {unreadCount > 0 && (
            <button
              className="btn btn-ghost w-full sm:w-auto"
              onClick={markAllAsRead}
            >
              <i className="fas fa-check-double mr-2" />
              <span className="text-xs sm:text-sm">Mark all read</span>
            </button>
          )}
          {selectedNotifications.length > 0 && (
            <button
              className="btn btn-ghost text-red-600 hover:text-red-700 dark:text-red-400 w-full sm:w-auto"
              onClick={handleDeleteSelected}
            >
              <i className="fas fa-trash mr-2" />
              <span className="text-xs sm:text-sm">
                Delete ({selectedNotifications.length})
              </span>
            </button>
          )}
          <button
            className="btn btn-danger w-full sm:w-auto"
            onClick={handleClearAll}
          >
            <i className="fas fa-trash-alt mr-2" />
            <span className="text-xs sm:text-sm">Clear All</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex gap-2">
            <button
              className={`px-4 py-2 rounded-lg transition-colors ${
                filter === "all"
                  ? "bg-primary-600 text-white"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              }`}
              onClick={() => setFilter("all")}
            >
              All
            </button>
            <button
              className={`px-4 py-2 rounded-lg transition-colors ${
                filter === "unread"
                  ? "bg-primary-600 text-white"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              }`}
              onClick={() => setFilter("unread")}
            >
              Unread
              {unreadCount > 0 && (
                <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-xs">
                  {unreadCount}
                </span>
              )}
            </button>
            <button
              className={`px-4 py-2 rounded-lg transition-colors ${
                filter === "read"
                  ? "bg-primary-600 text-white"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              }`}
              onClick={() => setFilter("read")}
            >
              Read
            </button>
          </div>

          <select
            className="input w-auto"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="new_ticket">New Tickets</option>
            <option value="ticket_reply">Ticket Replies</option>
            <option value="ticket_closed">Closed Tickets</option>
            <option value="invoice">Invoices</option>
            <option value="payment">Payments</option>
            <option value="customer">Customers</option>
            <option value="system">System</option>
          </select>
        </div>
      </div>

      {/* Notifications List */}
      <div className="card">
        {filteredNotifications.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 dark:bg-gray-700/50 rounded-full flex items-center justify-center">
              <i className="fas fa-bell-slash text-3xl text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No notifications
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              {filter === "unread"
                ? "You have no unread notifications"
                : filter === "read"
                  ? "You have no read notifications"
                  : "You don't have any notifications yet"}
            </p>
          </div>
        ) : (
          <>
            {/* Select All Bar */}
            <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <input
                    checked={
                      selectedNotifications.length ===
                        filteredNotifications.length &&
                      filteredNotifications.length > 0
                    }
                    className="rounded"
                    type="checkbox"
                    onChange={handleSelectAll}
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Select All ({filteredNotifications.length})
                  </span>
                </div>
              </div>
            </div>

            {/* Notifications */}
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              <AnimatePresence>
                {filteredNotifications.map((notification, index) => {
                  const iconConfig = getNotificationIcon(notification.type);
                  const isSelected = selectedNotifications.includes(
                    notification.id
                  );

                  return (
                    <motion.div
                      animate={{ opacity: 1, x: 0 }}
                      className={`px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${
                        !notification.read
                          ? "bg-blue-50/30 dark:bg-blue-900/10"
                          : ""
                      } ${isSelected ? "bg-primary-50 dark:bg-primary-900/20" : ""}`}
                      exit={{ opacity: 0, x: 20 }}
                      initial={{ opacity: 0, x: -20 }}
                      key={notification.id}
                      transition={{ delay: index * 0.05 }}
                    >
                      <div className="flex items-start gap-4">
                        {/* Checkbox */}
                        <input
                          checked={isSelected}
                          className="mt-1 rounded"
                          type="checkbox"
                          onChange={() =>
                            handleSelectNotification(notification.id)
                          }
                        />

                        {/* Icon */}
                        <div
                          className={`w-10 h-10 ${iconConfig.bg} rounded-full flex items-center justify-center text-white flex-shrink-0`}
                        >
                          <i className={`${iconConfig.icon} text-sm`} />
                        </div>

                        {/* Content */}
                        <div
                          className="flex-1 min-w-0 cursor-pointer"
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                  {notification.title}
                                </h3>
                                {!notification.read && (
                                  <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                                    New
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                {notification.message}
                              </p>
                              <div className="flex items-center gap-4 mt-2">
                                {notification.customer && (
                                  <span className="text-xs text-gray-500 dark:text-gray-500">
                                    <i className="fas fa-user mr-1" />
                                    {notification.customer}
                                  </span>
                                )}
                                <span className="text-xs text-gray-400 dark:text-gray-500">
                                  <i className="fas fa-clock mr-1" />
                                  {formatTime(notification.timestamp)}
                                </span>
                                <span className="text-xs text-gray-400 dark:text-gray-500">
                                  <i className="fas fa-tag mr-1" />
                                  {notification.type.replace("_", " ")}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          {!notification.read && (
                            <button
                              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                              title="Mark as read"
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.id);
                              }}
                            >
                              <i className="fas fa-check" />
                            </button>
                          )}
                          <button
                            className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 p-1"
                            title="Delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNotification(notification.id);
                            }}
                          >
                            <i className="fas fa-trash" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Notifications;
